import { Config } from './video-data/config';
import type Luminary from './';

export type ValidationResult = {
    passed: boolean;
    message: string;
};


/**
 * Fixes any duplicate media asset ids by generating new ids.
 */
export const fixUniqueMediaAssetIds = (luminary: Luminary) => {
    luminary
        .getAssetsByType(Config.MEDIA_ASSET_TYPE)
        .map((asset) => asset.generateNewId());
};

/**
 * Validates that we don't have any duplicate media asset ids. If we do then we can't find them using getAssetById which
 * is a problem.
 */
export const validateUniqueMediaAssetIds = (luminary: Luminary) => {
    const assetIds = luminary
        .getAssetsByType(Config.MEDIA_ASSET_TYPE)
        .map(asset => asset.id);
    const uniqueAssetIds = [...new Set(assetIds)];
    if (assetIds.length !== uniqueAssetIds.length) {
        return {
            passed: false,
            message: 'Media asset ids are not unique',
        };
    }
    return {
        passed: true,
        message: 'Media asset ids are unique',
    };
};

/**
 * Fixes scene group and media duration mismatches. This is a temporary fix until all precision errors are
 * fixed in production.
 */
export const fixSceneGroupDuration = (luminary: Luminary) => {
    luminary.sceneGroups
        .filter(
            sg => sg.mediaAsset
            && sg.mediaAsset.duration !== 0 // media asset is zero when not loaded
            && sg.duration > sg.mediaAsset.duration
        )
        .forEach(sg => {
            // precision error
            const diff = sg.duration - sg.mediaAsset.duration;
            if (diff < 1.0) { // small rounding error
                const lastScene = sg.lastScene();
                const lastSubscene = lastScene.subscenes[lastScene.subscenes.length - 1];
                lastSubscene.duration -= diff;
            }
            else { // didn't clean up all scenes
                while (sg.lastScene().endTime > sg.mediaAsset.duration) {
                    sg.removeScene(sg.lastScene().id);
                }
            }
        });
};

/**
 * Validates that the scene group duration is equal to the media asset duration. If this is not the case Luminary can't
 * properly play the scene group. Rendering will also fail as we won't be able to determine the correct duration of the
 * video.
 */
export const validateSceneGroupDuration = (luminary: Luminary) => {
    // validate scene group duration
    const result = luminary.sceneGroups
        .filter(
            sg => sg.mediaAsset
            && sg.mediaAsset.duration !== 0 // media asset is zero when not loaded
            && sg.duration > sg.mediaAsset.duration
        )
        .map(sg => {
            return `[ id: ${sg.id}, sg.duration: ${sg.duration}ms, sg.media.duration: ${sg.mediaAsset.duration}ms ]`;
        });

    // return validation result
    // TODO: this is a temporary implementation as we figure out how wide spread our TH issues are
    //  once we have a handle on that we should throw an error here
    if (result.length > 0) {
        return {
            passed: false,
            message: `Scene group duration is greater than media asset duration for: ${result.join(', ')}`,
        } as ValidationResult;
    }
    else {
        return {
            passed: true,
            message: '',
        } as ValidationResult;
    }
};
